import { OrganizationListEditModal } from "@/components/organization-list/OrganizationListEditModal.tsx";
import { useTitle } from "@/hooks/use-title.hook.ts";
import { useUnsavedChangesBlocker } from "@/hooks/use-unsaved-changes-blocker.hook.ts";
import analyticsService, { analyticEvents } from "@/services/analytics-service.ts";
import { useEffectOnce } from "react-use";
import {
  JobLevelSaveData,
  JobLevelsEditModal,
} from "@/pages/settings/jobs/levels/JobLevelsEditModal.tsx";
import { useJobLevelBulk, useJobLevels } from "@/queries/use-job-levels.query.ts";
import { useJobGrades, useJobTracks } from "@/queries/use-organization-lists.query.ts";
import { ApiJobLevel, ApiJobLevelBulkPayload } from "@/types/job-level.types.ts";
import {
  ApiOrganizationListItem,
  ApiOrganizationListType,
} from "@/types/organization-list.types.ts";
import {
  Box,
  Button,
  ButtonIcon,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Page,
  PageHeader,
  Stack,
  Table,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useModals,
} from "@skailer-com/uikit";
import { ColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

interface FrameworkRow {
  grade: ApiOrganizationListItem;
  levelsByTrack: Map<number, ApiJobLevel>;
}

interface LocalLevel extends ApiJobLevel {
  isNew?: boolean;
  isModified?: boolean;
  isDeleted?: boolean;
}

export interface JobFrameworkPageProps {
  mode?: "index" | "edit";
}

export function JobFrameworkPage({ mode = "index" }: JobFrameworkPageProps) {
  const { t } = useTranslation();
  const { openModal } = useModals();
  const navigate = useNavigate();
  const levelBulk = useJobLevelBulk();

  const isEditMode = mode === "edit";

  useTitle(t("core:career_architecture"));

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.jobCatalog.frameworkViewed);
  });

  const { data: gradesData, isLoading: isGradesLoading } = useJobGrades();
  const { data: tracksData, isLoading: isTracksLoading } = useJobTracks();
  const { data: levelsData, isLoading: isLevelsLoading } = useJobLevels();

  const grades = gradesData?.items.filter((g) => !g.is_archived) ?? [];
  const tracks = tracksData?.items.filter((tr) => !tr.is_archived) ?? [];
  const serverLevels = levelsData?.filter((l) => !l.is_archived) ?? [];

  const [localLevels, setLocalLevels] = useState<LocalLevel[]>([]);
  const [nextTempId, setNextTempId] = useState(-1);
  const [syncedEditMode, setSyncedEditMode] = useState(isEditMode);

  const hasUnsavedChanges =
    isEditMode && localLevels.some((level) => level.isNew || level.isModified || level.isDeleted);
  const { navigateWithoutBlocking } = useUnsavedChangesBlocker(hasUnsavedChanges);

  if (isEditMode && serverLevels.length > 0 && localLevels.length === 0) {
    setLocalLevels(serverLevels.map((l) => ({ ...l })));
  }

  if (isEditMode !== syncedEditMode) {
    setSyncedEditMode(isEditMode);
    if (!isEditMode) {
      setLocalLevels([]);
      setNextTempId(-1);
    }
  }

  const levels = isEditMode ? localLevels.filter((l) => !l.isDeleted) : serverLevels;

  const handleEditGrades = () => {
    openModal(OrganizationListEditModal, {
      type: ApiOrganizationListType.job_catalog_grades,
    });
  };

  const handleEditTracks = () => {
    openModal(OrganizationListEditModal, {
      type: ApiOrganizationListType.job_catalog_tracks,
    });
  };

  const handleEditLevels = () => {
    setLocalLevels(serverLevels.map((l) => ({ ...l })));
    navigate("/settings/jobs/framework/edit");
  };

  const handleCancel = () => {
    navigate("/settings/jobs/framework");
  };

  const handleSave = async () => {
    const payload: ApiJobLevelBulkPayload = {
      created: [],
      updated: [],
      deleted_ids: [],
    };

    localLevels.forEach((level) => {
      if (level.isDeleted && !level.isNew) {
        payload.deleted_ids.push(level.id);
      } else if (level.isNew && !level.isDeleted) {
        payload.created.push({
          title: level.title,
          track_id: level.track.id,
          grade_id: level.grade.id,
          sort: level.sort,
        });
      } else if (level.isModified && !level.isDeleted) {
        payload.updated.push({
          id: level.id,
          title: level.title,
          track_id: level.track.id,
          grade_id: level.grade.id,
          sort: level.sort,
        });
      }
    });

    if (
      payload.created.length === 0 &&
      payload.updated.length === 0 &&
      payload.deleted_ids.length === 0
    ) {
      navigate("/settings/jobs/framework");
      return;
    }

    await levelBulk.mutateAsync(payload);
    analyticsService.trackEvent(analyticEvents.jobCatalog.frameworkEdited);
    navigateWithoutBlocking(() => navigate("/settings/jobs/framework"));
  };

  const handleAddLevel = (gradeId: number, trackId: number) => {
    const grade = grades.find((g) => g.id === gradeId);
    const track = tracks.find((tr) => tr.id === trackId);
    if (!grade || !track) return;

    openModal(JobLevelsEditModal, {
      gradeId,
      trackId,
      onSave: (data: JobLevelSaveData) => {
        const newLevel: LocalLevel = {
          id: nextTempId,
          title: data.title,
          sort: 0,
          track: track,
          grade: grade,
          is_archived: false,
          isNew: true,
          roles_count: 0,
        };
        setLocalLevels((prev) => [...prev, newLevel]);
        setNextTempId((prev) => prev - 1);
      },
    });
  };

  const handleEditLevel = (level: LocalLevel) => {
    openModal(JobLevelsEditModal, {
      level,
      onSave: (data: JobLevelSaveData) => {
        setLocalLevels((prev) =>
          prev.map((l) => {
            if (l.id === level.id && level.title !== data.title) {
              return {
                ...l,
                title: data.title,
                isModified: !l.isNew ? true : l.isModified,
              };
            }
            return l;
          })
        );
      },
    });
  };

  const handleDeleteLevel = (level: LocalLevel) => {
    if (level.isNew) {
      setLocalLevels((prev) => prev.filter((l) => l.id !== level.id));
    } else {
      setLocalLevels((prev) =>
        prev.map((l) => (l.id === level.id ? { ...l, isDeleted: true } : l))
      );
    }
  };

  const tableData: FrameworkRow[] = grades.map((grade) => {
    const levelsByTrack = new Map<number, ApiJobLevel>();
    levels.forEach((level) => {
      if (level.grade.id === grade.id) {
        levelsByTrack.set(level.track.id, level);
      }
    });
    return { grade, levelsByTrack };
  });

  const columns = useMemo<ColumnDef<FrameworkRow>[]>(() => {
    const cols: ColumnDef<FrameworkRow>[] = [
      {
        id: "grade",
        header: t("core:grades"),
        accessorFn: (row) => row.grade.title,
        size: 150,
        cell: ({ row }) => (
          <Text className="font-semibold text-default">{row.original.grade.title}</Text>
        ),
      },
    ];

    tracks.forEach((track) => {
      cols.push({
        id: `track-${track.id}`,
        header: track.title,
        size: 160,
        cell: ({ row }) => {
          const level = row.original.levelsByTrack.get(track.id) as LocalLevel | undefined;
          const gradeId = row.original.grade.id;

          if (isEditMode) {
            return level ? (
              <Stack
                className="group"
                direction={"horizontal"}
                items={"baseline"}
                justify={"between"}
              >
                <Text
                  className={"hover:underline cursor-pointer"}
                  onClick={() => handleEditLevel(level)}
                >
                  {level.title || "-"}
                </Text>
                <Tooltip>
                  <TooltipTrigger>
                    <ButtonIcon
                      variant="secondary"
                      size="sm"
                      className="opacity-0 group-hover:opacity-100"
                      icon={<FontAwesomeIcon icon="fa-light fa-trash" />}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteLevel(level);
                      }}
                    />
                  </TooltipTrigger>
                  <TooltipContent className="text-sm">
                    <Stack>
                      <Text>
                        {t("core:used_by", {
                          target: t("plurals:job_profiles", { count: level.roles_count }),
                        })}
                      </Text>

                      {level.roles_count > 0 ? (
                        <Text className="text-light/60">{t("core:reassign_before_deleting")}</Text>
                      ) : (
                        <></>
                      )}
                    </Stack>
                  </TooltipContent>
                </Tooltip>
              </Stack>
            ) : (
              <Box className="group relative h-6">
                <Text className="opacity-100 group-hover:opacity-0">-</Text>
                <Button
                  variant="secondary"
                  className="opacity-0 group-hover:opacity-100 absolute left-0 top-1/2 -translate-y-1/2"
                  onClick={() => handleAddLevel(gradeId, track.id)}
                >
                  {t("common:add")}
                </Button>
              </Box>
            );
          }

          return level ? (
            <Tooltip>
              <TooltipTrigger>
                <Text className={"text-secondary"}>{level.title}</Text>
              </TooltipTrigger>
              <TooltipContent className="text-sm">
                {t("core:used_by", {
                  target: t("plurals:job_profiles", { count: level.roles_count }),
                })}
              </TooltipContent>
            </Tooltip>
          ) : (
            <Text className="text-secondary">-</Text>
          );
        },
      });
    });

    return cols;
  }, [tracks, isEditMode, localLevels]);

  const isLoading = isGradesLoading || isTracksLoading || isLevelsLoading;

  return (
    <Page className={"h-full overflow-auto relative flex-1 grow"}>
      <PageHeader title={isEditMode ? t("core:edit_levels") : t("core:career_architecture")}>
        <Stack direction={"horizontal"} gap={"sm"}>
          {isEditMode ? (
            <>
              <Button variant="secondary" onClick={handleCancel} disabled={levelBulk.isPending}>
                {t("common:cancel")}
              </Button>
              <Button onClick={handleSave} isLoading={levelBulk.isPending}>
                {t("common:save")}
              </Button>
            </>
          ) : (
            <DropdownMenu trigger={<Button>{t("common:edit")}</Button>}>
              <DropdownMenuItem
                title={t("core:grades")}
                description={t("core:manage_grade_scale")}
                onClick={handleEditGrades}
              />
              <DropdownMenuItem
                title={t("core:tracks")}
                description={t("core:manage_job_tracks")}
                onClick={handleEditTracks}
              />
              <DropdownMenuItem
                title={t("core:levels")}
                description={t("core:setup_grade_names_for_each_track")}
                onClick={handleEditLevels}
              />
            </DropdownMenu>
          )}
        </Stack>
      </PageHeader>

      <Page.Content>
        {grades.length === 0 || tracks.length === 0 ? (
          <Stack className="p-6" gap="md">
            <Text className="text-secondary">{t("core:career_framework_empty_description")}</Text>
          </Stack>
        ) : (
          <Table data={tableData} columns={columns} showSkeleton={isLoading} />
        )}
      </Page.Content>
    </Page>
  );
}
